import CN from "classnames";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/Button";
import ImageUpload, { Image } from "./plugins/ImageUpload";
import { useSelector } from "react-redux";
import store from "../../store/store";
import { Oval } from "react-loader-spinner";
import { addCategory } from "../Categories/reducer/categorySlice";
import { editCategory } from "./reducer/categoryDetailsSlice";

interface FormValues {
  category: string;
  description: string;
  file?: File[] | string;
  isPopular: boolean;
}

export const EditCategory: React.FC<any> = () => {
  const { categoryData } = useSelector(
    (state: any) => state.rootReducer?.category
  );

  const { categoryDetails, isLoading, isSuccess, editProductRes } = useSelector(
    (state: any) => state.rootReducer?.categoryDetail
  );
  const navigate = useNavigate();

  console.log('categoryDetails', categoryDetails?.category)

  // Validation schema
  const validationSchema = Yup.object({
    category: Yup.string()
      .required("Category Name is required")
      .max(50, "Category Name must be 50 characters or less"),
    description: Yup.string()
      .required("Description is required")
      .max(30, "Description must be 30 characters or less")
      .min(10, "Description must be at least 10 characters long"),
    // images: Yup.array().min(1, "At least one image is required"),
    // logo: Yup.array(),
  });

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    values,
    errors,
    touched,
    dirty
  } = useFormik<FormValues>({
    initialValues: {
      category: categoryDetails?.category,
      description: categoryDetails?.description,
      file: categoryDetails?.logo,
      isPopular: categoryDetails?.isPopular,
    },
    validationSchema,
    onSubmit: (values: FormValues) => {
      const formData = new FormData();
      console.log("values", values);

      const convertToFile = async (image: { path: string; public_id: string }): Promise<File> => {
        // console.log(image,"convert image");
        const fileName = image.path.split("/").pop() || "uploaded_file";
        const response = await fetch(image.path);
        const blob = await response.blob();
        // console.log("convert hello", response, blob);
        return new File([blob], fileName, { type: blob.type });
      };
      

      (async () => {
        for (const [key, value] of Object.entries(values)) {
          if (key === "file") {
            // Process the single image (File or existing image object)
            // console.log("uploaded image", value);
            if (value) {
              if (value instanceof File) {
                // New image, add directly
                formData.append("file", value);
              } else {
                // Existing image, convert to File
                const file = await convertToFile(value as { path: string; public_id: string });
                // console.log("testing file convert",file);
                formData.append("file", file);
              }
            }
          } else if (Array.isArray(value)) {
            // Handle other array fields
            value.forEach((val) => formData.append(`${key}[]`, val));
          } else {
            // Handle other fields
            formData.append(key, value as string);
          }
        }
    
        // Dispatch the action with the formData
        store.dispatch(
          editCategory({ formData: formData, categoryId: categoryDetails?._id })
        );
      })();
  }
}
);

  const handleImageUpload = (image: File | Image | null) => {
    console.log("image", image);
    setFieldValue("file", image);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <form onSubmit={handleSubmit}>
        <div className="lg:grid grid-cols-2 flex flex-col gap-6 my-4 py-8 lg:mx-8 mx-4">
          {/* Category Logo */}
          <div className="col-span-2">
            <label className="block text-base font-semibold mb-2">
              Category Logo <span className="text-red-500">*</span>
            </label>

            <ImageUpload onUpload={handleImageUpload} />

            {errors.file && touched.file && (
              <p className="text-red-500 text-sm my-2">{errors.file}</p>
            )}
          </div>

          {/* Product Name */}
          <div className="col-span-1">
            <label className="block text-base font-semibold mb-2">
              Category Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="category"
              value={values.category}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Untitled category"
              className={CN(
                "block w-full p-3 border rounded-md",
                errors.category && touched.category
                  ? "border-red-500"
                  : "border-primary1"
              )}
            />
            {errors.category && touched.category && (
              <p className="text-red-500 text-sm my-2">{errors.category}</p>
            )}
          </div>

          {/* Description */}
          <div className="col-span-1">
            <div className="flex flex-col gap-1 mb-2">
              <label className="block text-base font-semibold">
                Description
                <span className="text-red-500">*</span>
              </label>
            </div>
            <textarea
              name="description"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              className={CN(
                "block w-full p-3 border rounded-md",
                errors.description && touched.description
                  ? "border-red-500"
                  : "border-primary1"
              )}
            />
            {errors.description && touched.description && (
              <p className="text-red-500 text-sm my-2">{errors.description}</p>
            )}
          </div>

          {/* Popular Product */}
          <div className="col-span-2 flex items-start">
            <input
              type="checkbox"
              name="isPopular"
              className="accent-primary1 w-[18px] h-[18px] mr-3 my-1 cursor-pointer rounded border border-primary1"
              onChange={(e) => setFieldValue("isPopular", e.target.checked)}
              checked={values.isPopular}
            />
            <div className="flex flex-col gap-1 mb-2">
              <label className="block text-base font-semibold">
                Popular Category
              </label>
              <span className="font-normal text-sm">
                Mark this category as popular
              </span>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div
          className={`flex justify-end flex-row gap-4 mt-5 py-4 w-full sticky bottom-0 bg-primary2 lg:px-8 px-4`}
        >
          <div className="col-span-2 flex justify-end gap-5">
            <Button onClick={() => navigate("/categories")} apparence="gray">
              Cancel
            </Button>

            <Button
              isLoading={isLoading.categoryEdit}
              view="default"
              type="submit"
              disabled={!dirty}
            >
              <div className="flex gap-1">
                {isLoading.categoryEdit && (
                  <Oval
                    height={18}
                    width={18}
                    color="#ffff"
                    wrapperStyle={{}}
                    wrapperClass="max-sm:mt-[3.5px] mt-[5.2px] max-lg:mt-[5.2px]"
                    visible={true}
                    ariaLabel="oval-loading"
                    secondaryColor="gray-primary"
                    strokeWidth={6}
                    strokeWidthSecondary={6}
                  />
                )}
                <div className="font-normal text-base">Save</div>
              </div>
            </Button>
          </div>
        </div>
      </form>
    </motion.div>
  );
};

export default EditCategory;
